import React from "react";
import PageRecord from "models/view-models/page-record";
import moment from "moment";
import useToggleNotebookReferencePanel from "../notebook-reference-panel/use-toggle-notebook-reference-panel";
import useToggleNotebookSidebar from "../use-toggle-notebook-sidebar";
import useUser from "utilities/hooks/domain/users/use-user";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { useHistory } from "react-router-dom";
import { useLocalization } from "utilities/hooks/use-localization";
import usePageViewSettings from "./use-page-view-settings";
import { ViewModes } from "models/enumerations/view-modes/view-modes";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { useGetPageRoute } from "utilities/hooks/domain/notebooks/pages/use-get-page-route";
import RemoteImage from "atoms/images/remote-image";
import Icon from "atoms/icons/icon";
import PageContextMenu from "pages/notebooks/pages/page-context-menu";

export interface PageListItemProps {
    page: PageRecord;
}

const CSS_CLASS_NAME = "c-page-list-item";
export const PAGE_LIST_ITEM_DATA_TEST_ID = "page-list-item";
export const PAGE_LIST_ITEM_MENU_BUTTON_DATA_TEST_ID =
    "page-list-item-menu-button";

const PageListItem: React.FC<PageListItemProps> = (props) => {
    const { page } = props;
    const { t } = useLocalization();
    const history = useHistory();
    const { user: owner } = useUser(page.ownerId);
    const { showPanel } = useToggleNotebookReferencePanel();
    const { showSidebar } = useToggleNotebookSidebar();
    const { viewMode } = usePageViewSettings();
    const viewSetting = viewMode === ViewModes.List ? "-list" : "-grid";
    const bothPanelsOpen = showPanel && showSidebar;
    const modifiers = `${viewSetting} ${bothPanelsOpen ? "-both-panels-open" : ""}`;
    const { currentIdentity } = useCurrentIdentity();
    const isOwner = currentIdentity?.userId() === page.ownerId;
    const { getPageRoute } = useGetPageRoute();

    const pageOwner = isOwner ? t("you") : owner?.getFirstNameAndLastInitial();

    const formatTimeAgo = (): React.ReactNode => {
        if (!page.createdOn) {
            return t("addedJustNow");
        }
        return (
            <>
                {t("added")} {moment().to(moment(page.createdOn))} by{" "}
                <strong>{pageOwner}</strong>
            </>
        );
    };

    const handleOpenPage = () => {
        const route = getPageRoute(page);

        if (route != null) {
            history.push(route);
        }
    };

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const formattedName =
        page.name.length > 24 && viewSetting === "-grid"
            ? page.name.slice(0, 21) + "..."
            : page.name;

    return (
        <div
            className={`${CSS_CLASS_NAME} ${modifiers}`}
            data-test-id={PAGE_LIST_ITEM_DATA_TEST_ID}
            onClick={handleOpenPage}>
            <div className={`${CSS_CLASS_NAME}__cover-image-wrapper`}>
                {page.coverImageFileId ? (
                    <RemoteImage
                        cssClassName={`${CSS_CLASS_NAME}__cover-image`}
                        fileId={page.coverImageFileId}
                        imageLoadingType={"lazy"}
                    />
                ) : (
                    <div className={`${CSS_CLASS_NAME}__cover-image -default`}>
                        <Icon type={Icons.Image} size={IconSizes.XLarge} />
                    </div>
                )}
            </div>
            <div className={`${CSS_CLASS_NAME}__header`}>
                <div className={`${CSS_CLASS_NAME}__content`}>
                    <h3 className={`${CSS_CLASS_NAME}__title`}>
                        {formattedName}
                    </h3>
                    <div className={`${CSS_CLASS_NAME}__subtitle`}>
                        <div>{formatTimeAgo()}</div>
                    </div>
                </div>
                <div
                    className={`${CSS_CLASS_NAME}__menu`}
                    data-test-id={PAGE_LIST_ITEM_MENU_BUTTON_DATA_TEST_ID}
                    onClick={handleMenuClick}>
                    <PageContextMenu
                        menuButtonCssClassName={`${CSS_CLASS_NAME}__action-menu`}
                        page={page}
                        isViewOnly={!isOwner}
                        dataTestId="page-list-item--context-menu"
                    />
                </div>
            </div>
        </div>
    );
};

export default PageListItem;
